import Vue from 'vue';
import Router from 'vue-router';
import Meta from 'vue-meta';
import VueAnalytics from 'vue-ua';
import { getYPositionElement } from '@/shared/utils';
import Store from '@/store/index';

Vue.use(Meta);
Vue.use(Router);

const Dashboard = () => import('@/components/dashboard/dashboard');
const WriteReview = () => import('@/components/review/review-create/review-create');
// const BrandDetail = () => import('@/components/brand-detail/brand-detail');
const BrandDetail = () => import('@/components/v2/brand-detail-v2');
const BrandDetailOrigin = () => import('@/components/brand-detail/brand-detail-origin');
const Brands = () => import('@/components/brands/brands');
const BrandsLetter = () => import('@/components/brands/brandsLetter/brandsLetter');
const AddProduct = () => import('@/components/product/add-product/add-product');
const productDetail = () => import('@/components/product/product-detail/product-detail');
const Category = () => import('@/components/v2/category-detail-v2');
const Search = () => import('@/components/search/search');
const NotFound = () => import('@/components/static/not-found');
const RegisterDetail = () => import('@/components/register-detail/register-detail');
const Recommendation = () => import('@/components/beauty-profile/recommendation');
const ExpertPage = () => import('../components/expert-page/expert-page');
const ExpertUsers = () => import('../components/expert-page/expert-users');
const OurCuratedSelections = () => import('@/components/our-curated-selections/our-curated-selections');
const ReviewDetail = () => import('@/components/review/review-detail/review-detail');

const EmptyParentComponent = {
  template: '<router-view :key="$route.fullPath"></router-view>',
};

const router = new Router({
  mode: 'history',
  routes: [
    {
      path: '/',
      name: 'Dashboard',
      component: Dashboard,
      meta: {
        title: 'Review',
        header: true,
      },
    },
    {
      path: '/brands',
      name: 'Brands',
      component: Brands,
    },
    {
      path: '/add-product',
      name: 'AddProduct',
      component: AddProduct,
      meta: {
        title: 'Review',
        header: true,
      },
    },
    {
      path: '/brand/:brand_id-:brand_name',
      name: 'BrandDetailOrigin',
      component: BrandDetailOrigin,
      meta: {
        title: 'Brands Origin',
        header: true,
      },
    },
    {
      path: '/brand/:brand_id/:brand_name',
      name: 'BrandDetail',
      component: BrandDetail,
      meta: {
        title: 'Brands',
        header: true,
      },
    },
    {
      path: '/brands/:letter',
      name: 'BrandsLetter',
      props: true,
      component: BrandsLetter,
      meta: {
        title: 'Brands Letter',
        header: true,
      },
    },
    {
      path: '/product/:category_name/:product_id',
      name: 'productDetail',
      component: productDetail,
      meta: {
        title: 'Detail Product | SOCO by Sociolla',
        header: true,
        footer: false,
      },
    },
    {
      path: '/review',
      name: 'WriteReviewRoot',
      component: EmptyParentComponent,
      children: [
        {
          path: 'add',
          name: 'WriteReviewProduct',
          component: WriteReview,
          meta: {
            title: 'Review | SOCO by Sociolla',
            header: true,
            footer: false,
          },
          children: [
            {
              path: ':product_id',
              component: WriteReview,
              name: 'WriteReview',
              meta: {
                title: 'Review | SOCO by Sociolla',
                header: true,
                footer: false,
              },
            },
          ],
        },
      ],
    },
    {
      path: '/review/:id',
      name: 'ReviewDetail',
      component: ReviewDetail,
      meta: {
        title: 'Review | SOCO by Sociolla',
        header: true,
        footer: false,
      },
    },
    {
      path: '/category/:category_id/:category_name',
      name: 'Category',
      component: Category,
      meta: {
        title: 'Review | SOCO by Sociolla',
        header: true,
      },
    },
    {
      path: '/search',
      name: 'Search',
      component: Search,
      meta: {
        title: 'Review | SOCO by Sociolla',
        header: true,
      },
    },
    {
      path: '/tag/:name',
      name: 'TagConcern',
      component: Search,
      meta: {
        title: 'Review | SOCO by Sociolla',
        header: true,
      },
    },
    {
      path: '/beauty-profile',
      name: 'BeautyProfile',
      // BP & My Interest hidden on web: https://new-sociolla.atlassian.net/browse/NON-16072
      redirect: '/',
    },
    {
      path: '/our-curated-selections',
      name: 'OurCuratedSelections',
      component: OurCuratedSelections,
      meta: {
        title: 'Our Curated Selections | SOCO by Sociolla',
      },
    },
    {
      path: '/our-curated-selection/:category_id',
      name: 'ProductMatchCategory',
      component: Category,
      meta: {
        title: 'Review | SOCO by Sociolla',
        header: true,
      },
    },
    {
      path: '/register-detail',
      name: 'RegisterDetail',
      component: RegisterDetail,
      meta: {
        title: 'Register Detail | SOCO by Sociolla',
      },
    },
    {
      path: '/recommendation',
      name: 'Recommendation',
      component: Recommendation,
      meta: {
        title: 'Review | SOCO by Sociolla',
      },
    },
    {
      path: '/expert',
      name: 'ExpertPage',
      component: ExpertPage,
      meta: {
        title: 'Expert Review | SOCO by Sociolla',
      },
    },
    {
      path: '/expert/users',
      name: 'ExpertUsers',
      component: ExpertUsers,
      meta: {
        title: 'Expert Users | SOCO by Sociolla',
      },
    },
    {
      path: '*',
      name: 'NotFound',
      component: NotFound,
      meta: {
        title: 'Not Found - SOCO by Sociolla',
        header: true,
        footer: false,
      },
    },
  ],
  scrollBehavior(to, from, savedPosition) {
    const inProductDetailPage = to.name === 'productDetail' && from.name === 'productDetail';
    // const inAddProductPage = from.name === 'AddProduct' && to.name !== 'AddProduct';

    if (inProductDetailPage) {
      const paginationHasUpdated = Boolean(to.query.page) && to.query.page !== from.query.page;
      if (paginationHasUpdated) {
        const seeAllReviewYPosition = getYPositionElement(document.getElementById('seeAllReview'));
        const headerOffset = 100;
        return { x: 0, y: seeAllReviewYPosition - headerOffset };
      }
      return { x: 0, y: 0 };
    } else {
      if (savedPosition) {
        return savedPosition;
      }

      return { x: 0, y: 0 };
    }
  },
});

Vue.use(VueAnalytics, {
  appName: 'SOCO Review',
  appVersion: 'none',
  trackingId: 'UA-57294171-1',
  debug: false,
  trackPage: true | false,
});

router.beforeEach((to, from, next) => {
  if (to.name !== from.name) {
    Store.commit('setHeaderMeta', '');
  } else {
    Store.commit('setTitle', '');
  }
  if (from.name === 'productDetail' && to.name === 'WriteReview' && Store.state.auth.loggedin) {
    return Store.dispatch('userBlockedToReviewCheck').then((isBlocked) => {
      if (isBlocked) {
        return false;
      } else {
        return next();
      }
    });
  }

  if (from.name !== 'AddProduct' || !Store.state.auth.loggedin || !Store.state.addProduct.isFormHasBeenFilled) {
    next();
  } else {
    if (!Store.state.modal.discardAddProduct.data && to.name && to.name !== 'AddProduct') {
      Store.commit('setDataModal', {
        modalName: 'discardAddProduct',
        value: true,
      });
      Store.commit('setRedirectURL', to.fullPath);
      Store.commit('toggleModalGlobalState', {
        modalName: 'discardAddProduct',
      });
    } else {
      Store.commit('setDataModal', {
        modalName: 'discardAddProduct',
        value: false,
      });
      next();
    }
  }
});

router.afterEach((to, from) => {
  setTimeout(function () {
    Vue.analytics.trackView(to.path, true);
  }, 400);
  if (typeof googletag === 'object' && 'pubads' in googletag && !loadFirstAds) {
    googletag.pubads().refresh();
  }
  if (loadFirstAds) {
    loadFirstAds = false;
  }
  const { fullPath, meta, name, params, path, query } = to;
  Store.commit('currentUrl', { fullPath, meta, name, params, path, query });
});

export default router;
